import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, EyeOff, Check, X, Mail } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import { ROLE_OPTIONS, DSP_CRITERION, isFamilyRole, isDspRole } from '../../lib/roleLabels';
import { ZIP_NOTICE, cleanZip, isValidZip } from '../../lib/zipCode';
import { glassCard, bgStyle } from '../../lib/onboardingStyles';
import { reportError } from '../../lib/errorReporting';
import { isValidPassword } from '../../lib/passwordRules';
import PasswordHints from '../common/PasswordHints';
import { buildPendingSignup, confirmRedirect } from '../../lib/signupCompletion';

// Brand-kit interface icons (assets/brand/new-kit/ui-icons, 24-grid, 1.8 stroke).
const KIT_ICON_PATHS = {
  video: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m10 9 6 3-6 3Z" /></>,
  community: <><circle cx="9" cy="8" r="3" /><path d="M3 21v-3a6 6 0 0 1 12 0v3M16 5a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 5v2" /></>,
  events: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 10h18M7 14h2M13 14h2M7 18h2" /></>,
};
const KitIcon = ({ name, size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {KIT_ICON_PATHS[name]}
  </svg>
);

const valueSlides = [
  {
    icon: 'video',
    title: 'Learn from fellow DSPs',
    description: 'Watch short videos and real stories from professionals who get it — and share your own.',
  },
  {
    icon: 'community',
    title: 'Join the conversation',
    description: 'Find your Table — safe, moderated spaces to ask questions, chat with other DSPs, and celebrate wins.',
  },
  {
    icon: 'events',
    title: 'Grow your career',
    description: 'Earn badges, find trainings and events, and soon, connect with a mentor.',
  },
];


// Steps (open sign-up, 064 — no access-code screen; interests moved to the
// profile editor, where Complete Profile still asks for them):
//   0 = value props
//   1 = email + password + terms
//   2 = about you: name + handle + display preference
//   3 = role + years
//   4 = agency + zip + join (sign-up runs here, once)
//   5 = check your email — only when email confirmation is on (signUp
//       returned no session)
// With a session, join goes straight to /feed; AppShell finishes sign-up
// (src/lib/signupCompletion.js) and sends DSPs a code didn't verify to
// /get-verified. Without one, the same happens on their first sign-in.
const TOTAL_FORM_STEPS = 4; // steps 1–4
const CHECK_EMAIL_STEP = 5;

// The agency step's wording by role. Family roles are asked where they live
// (isFamilyRole, 056/057); agency staff where they work; advocates and "Other"
// may have no workplace that fits, so they're asked where they're based.
const agencyStepCopy = (role) => {
  if (isFamilyRole(role)) return {
    title: 'Where are you located?',
    subtitle: 'Helps us connect you with local resources and groups.',
    placeholder: 'City, State',
  };
  if (isDspRole(role)) return {
    title: 'Where do you work?',
    subtitle: 'Helps fellow DSPs know where you\'re coming from.',
    placeholder: 'Agency or organization name',
  };
  if (role === 'agency_staff') return {
    title: 'Where do you work?',
    subtitle: 'Helps members know where you\'re coming from.',
    placeholder: 'Agency or organization name',
  };
  return {
    title: 'Where are you based?',
    subtitle: 'An organization, school or city, whichever fits. Helps members know where you\'re coming from.',
    placeholder: 'Organization, or City, State',
  };
};

// A handle suggested from the name ("Jane Doe" → jane_doe), until the member
// edits the handle themselves. The availability check runs on it as usual.
const suggestHandle = (name) => name
  .toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .trim()
  .replace(/\s+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '')
  .slice(0, 30);

// signUp's errors in words a member can act on. Raw Supabase messages ("Email
// rate limit exceeded") used to show as-is; launch day may hit the rate limit.
const signUpErrorMessage = (error) => {
  const msg = error?.message || '';
  if (/already registered/i.test(msg)) return 'An account with this email already exists. Try signing in.';
  if (error?.status === 429 || /rate limit|too many/i.test(msg)) {
    return 'Too many sign-ups at once right now. Please wait a minute and try again.';
  }
  if (/failed to fetch|network|load failed/i.test(msg)) {
    return "We couldn't reach DSP Cafe. Check your connection and try again.";
  }
  if (/password/i.test(msg)) return "That password wasn't accepted. Go back and choose another one.";
  if (/email/i.test(msg)) return "That email address wasn't accepted. Go back and check it.";
  return 'Something went wrong creating your account. Please try again.';
};

const OnboardingScreen = () => {
  const navigate = useNavigate();
  const { session, loading, userData, setUserData, signUp, signIn } = useUser();
  const [step, setStep] = useState(0);
  // 'idle' | 'sending' | 'sent' | 'error' — the check-your-email step's resend
  const [resendState, setResendState] = useState('idle');
  // 'idle' | 'checking' | 'unconfirmed' — the check-your-email step's sign-in
  const [confirmState, setConfirmState] = useState('idle');

  // Already signed in when the screen opened (a refresh mid-onboarding, or a
  // stray link) → the feed, not onboarding. Checked once, when the auth
  // check settles: handleJoin's own sign-up must not trip it.
  const sessionChecked = useRef(false);
  useEffect(() => {
    if (loading || sessionChecked.current) return;
    sessionChecked.current = true;
    if (session) navigate('/feed', { replace: true });
  }, [loading, session, navigate]);
  const [slideIndex, setSlideIndex] = useState(0);
  const slideRef = useRef(null);
  const inputRef = useRef(null);

  // Carousel drag-to-scroll (mouse + touch)
  const dragStart = useRef(null);

  const handleSlidePointerDown = (e) => {
    dragStart.current = { x: e.clientX, scrollLeft: slideRef.current.scrollLeft };
    slideRef.current.setPointerCapture(e.pointerId);
    slideRef.current.style.cursor = 'grabbing';
  };

  const handleSlidePointerMove = (e) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    slideRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
  };

  const handleSlidePointerUp = () => {
    dragStart.current = null;
    if (slideRef.current) slideRef.current.style.cursor = 'grab';
  };

  // Years of experience (collected on role step)
  const [yearsExperience, setYearsExperience] = useState('');

  // Username + display preference (step 2)
  const [username, setUsername] = useState('');
  const handleTouched = useRef(false);
  const [useUsername, setUseUsername] = useState(false);
  // 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid_format'
  const [usernameStatus, setUsernameStatus] = useState('idle');

  const [termsAccepted, setTermsAccepted] = useState(false);

  // Sign-up is open (064), but approved landing sign-ups still get an emailed
  // /onboarding?code=… link (send-signup-invite). A code that came that way is
  // applied at join: it verifies if an admin marked it to (058), and does
  // nothing else otherwise. There is no code screen any more.
  const [searchParams] = useSearchParams();
  const [accessCode] = useState(() => (searchParams.get('code') ?? '').trim().slice(0, 64));

  // Email/password local state (not stored in UserContext until account is created)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [zipCode, setZipCode] = useState('');

  // Auto-focus inputs when step changes
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [step]);

  // Handle slide scroll tracking
  useEffect(() => {
    const container = slideRef.current;
    if (!container) return;
    const handleScroll = () => {
      const cardWidth = container.children[0]?.offsetWidth || 1;
      setSlideIndex(Math.round(container.scrollLeft / cardWidth));
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [step]);

  // Debounced username availability check
  useEffect(() => {
    if (username.length < 3) { setUsernameStatus('idle'); return; }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc('check_username_available', { p_username: username });
      setUsernameStatus(data ?? 'taken');
    }, 500);
    return () => clearTimeout(t);
  }, [username]);

  const handleUsernameChange = (val) => {
    const next = val.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 30);
    // Clearing the field hands it back to the suggestion.
    handleTouched.current = next !== '';
    setUsername(next);
    setUsernameStatus('idle');
  };

  const handleNameChange = (val) => {
    setUserData({ ...userData, name: val });
    if (!handleTouched.current) {
      setUsername(suggestHandle(val));
      setUsernameStatus('idle');
    }
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => {
    if (step === 0) {
      navigate('/');
    } else {
      setStep(s => s - 1);
    }
  };

  // Step 1: catch an existing account now rather than at join (055). Needs no
  // code since 064. NULL / an error means "couldn't check" — carry on; sign-up
  // still catches it.
  const handleEmailContinue = async () => {
    setEmailChecking(true);
    const { data } = await supabase.rpc('email_has_account', {
      p_code: accessCode || null,
      p_email: email.trim(),
    });
    setEmailChecking(false);
    if (data === true) { setEmailTaken(true); return; }
    nextStep();
  };

  // skipZip: "Skip and join" must not submit a zip — submitting is the opt-in.
  //
  // signUp runs once, here. Everything that needs a session (terms record,
  // years, zip, a ?code=) rides in the metadata as pending_signup and is
  // applied on the first signed-in session — straight away while email
  // confirmation is off, after confirming once it is on. One path either way.
  const handleJoin = async ({ skipZip = false } = {}) => {
    if (submitting) return;
    setSignUpError('');
    setSubmitting(true);

    const profileData = {
      name:         userData.name,
      role:         userData.role,
      agency:       userData.agency,
      username:     username.trim(),
      use_username: useUsername,
    };
    const pending = buildPendingSignup({
      termsVersion: TERMS_VERSION,
      yearsExperience: yearsExperience !== '' && isDspRole(userData.role) ? parseInt(yearsExperience, 10) : null,
      zip: !skipZip && isValidZip(zipCode) && isDspRole(userData.role) ? zipCode : null,
      code: accessCode || null,
    });

    const { data: signUpData, error } = await signUp(email.trim(), password, profileData, pending);
    setSubmitting(false);
    if (error) {
      // An existing account is expected; anything else (rate limits, outages)
      // is what we need to see on launch day.
      if (!error.message.includes('already registered')) reportError(error, 'api', 'signUp');
      setSignUpError(signUpErrorMessage(error));
      return;
    }
    // With confirmation on, Supabase doesn't say "already registered": it
    // returns a stand-in user with no identities and sends nothing.
    if (signUpData?.user && Array.isArray(signUpData.user.identities) && signUpData.user.identities.length === 0) {
      setSignUpError('An account with this email already exists. Try signing in.');
      return;
    }
    if (signUpData?.session) {
      navigate('/feed', { replace: true });
    } else {
      setStep(CHECK_EMAIL_STEP);
    }
  };

  // "I've confirmed": sign in with what they just typed instead of sending
  // them to an empty login form. On iPhone the link opens in Safari, which
  // gets the session and finishes sign-up there, so the app has none.
  // Supabase refuses an unconfirmed account (email_not_confirmed), so this
  // can't skip confirmation. Safari has already cleared pending_signup, so
  // useFinishSignup won't offer verification here: decide from the profile.
  const handleConfirmedSignIn = async () => {
    if (!password) { navigate('/login', { replace: true, state: { email: email.trim() } }); return; }
    setConfirmState('checking');
    const { error } = await signIn(email.trim(), password);
    if (error) {
      if (error.code === 'email_not_confirmed' || /not confirmed/i.test(error.message || '')) {
        setConfirmState('unconfirmed');
        return;
      }
      navigate('/login', { replace: true, state: { email: email.trim() } });
      return;
    }
    const { data: { session: newSession } } = await supabase.auth.getSession();
    const { data: profile } = newSession
      ? await supabase.from('profiles').select('role, verified, verification_status')
        .eq('id', newSession.user.id).maybeSingle()
      : { data: null };
    const offer = profile && isDspRole(profile.role) && !profile.verified
      && (profile.verification_status ?? 'none') === 'none';
    navigate(offer ? '/get-verified' : '/feed', { replace: true });
  };

  const handleResend = async () => {
    setResendState('sending');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: { emailRedirectTo: confirmRedirect() },
    });
    setResendState(error ? 'error' : 'sent');
  };

  const TERMS_VERSION = '2026-10-30'; // = the Terms' effective date (landing/terms.html); change both together
  const passwordValid = isValidPassword(password);
  // Checked here, on the email step — sign-up itself only runs at join, so a
  // bad address would otherwise surface three screens later.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const emailPasswordValid = emailValid && passwordValid && termsAccepted;
  // Says why Continue is greyed out, first thing missing only.
  const continueHint = !emailValid ? 'Enter your email to continue.'
    : !passwordValid ? 'Finish the password checklist to continue.'
    : !termsAccepted ? 'Agree to the terms to continue.'
    : null;

  // Progress bar — form steps 1–4; full on the check-your-email step
  const progress = step === 0 ? 0 : Math.min(1, step / TOTAL_FORM_STEPS);

  return (
    <div className="min-h-screen flex flex-col" style={bgStyle}>
      {/* Top bar: back + progress. The status-bar inset (--sat) is ours to add:
          the iOS app draws under the status bar (contentInset 'never'). */}
      <div className="px-4 pb-2" style={{ paddingTop: 'calc(var(--sat) + 1rem)' }}>
        <div className="flex items-center gap-3 mb-3">
          {/* No way back from check-your-email: the account exists, and the
              join step would run sign-up again. */}
          {step !== CHECK_EMAIL_STEP && (
            <button
              onClick={prevStep}
              aria-label="Back"
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-95"
              style={{
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
          )}
          {step === 0 && (
            <>
              <img src="/brand/mark-cup.svg" alt="" className="w-9 h-9 mx-auto" />
              <span className="w-9" aria-hidden="true" />
            </>
          )}
          {step > 0 && (
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${progress * 100}%`,
                  backgroundColor: 'var(--brand-primary)',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col px-6 pb-6 max-w-lg mx-auto w-full">

        {/* Step 0: Value Proposition Slides */}
        {step === 0 && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-6">
              <p className="text-[13px] font-bold uppercase mb-1.5" style={{ letterSpacing: '0.12em', color: 'var(--brand-secondary)' }}>
                Welcome to DSP Cafe
              </p>
              <h2 className="text-[26px] font-extrabold text-white leading-tight">
                Real DSPs.<br />Real conversations.
              </h2>
            </div>

            <div
              ref={slideRef}
              className="flex overflow-x-auto scrollbar-hide gap-4 mb-5 -mx-2 px-2"
              style={{ scrollSnapType: 'x mandatory', cursor: 'grab', userSelect: 'none' }}
              onPointerDown={handleSlidePointerDown}
              onPointerMove={handleSlidePointerMove}
              onPointerUp={handleSlidePointerUp}
              onPointerLeave={handleSlidePointerUp}
            >
              {/* Brand kit (2026-09-30): a quiet dark card instead of the grey
                glass placeholder — testers found a white card too bright. */}
            {valueSlides.map((slide, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 w-full rounded-[20px] p-6"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(var(--brand-secondary-rgb), 0.3)',
                  scrollSnapAlign: 'start',
                }}
              >
                <div
                  className="w-[52px] h-[52px] mb-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(var(--brand-secondary-rgb), 0.2)', color: '#CFE3D6' }}
                >
                  <KitIcon name={slide.icon} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2.5">{slide.title}</h3>
                <p className="text-[15px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>
                  {slide.description}
                </p>
              </div>
            ))}
            </div>

            <div className="flex items-center justify-center gap-4 mb-8">
              <button
                onClick={() => {
                  const el = slideRef.current;
                  if (!el) return;
                  el.scrollTo({ left: el.children[0]?.offsetWidth * (slideIndex - 1), behavior: 'smooth' });
                }}
                disabled={slideIndex === 0}
                aria-label="Previous slide"
                className="p-1 transition-all active:scale-95 disabled:opacity-20"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex items-center gap-2">
                {valueSlides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const el = slideRef.current;
                      if (!el) return;
                      el.scrollTo({ left: el.children[0]?.offsetWidth * idx, behavior: 'smooth' });
                    }}
                    aria-label={`Go to slide ${idx + 1}`}
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: idx === slideIndex ? '2rem' : '0.5rem',
                      backgroundColor: idx === slideIndex ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    }}
                  />
                ))}
              </div>

              <button
                onClick={() => {
                  const el = slideRef.current;
                  if (!el) return;
                  el.scrollTo({ left: el.children[0]?.offsetWidth * (slideIndex + 1), behavior: 'smooth' });
                }}
                disabled={slideIndex === valueSlides.length - 1}
                aria-label="Next slide"
                className="p-1 transition-all active:scale-95 disabled:opacity-20"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <button
              onClick={nextStep}
              className="w-full py-3.5 rounded-2xl font-semibold text-base transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-secondary)', color: 'var(--gradient-base)' }}
            >
              Get Started
            </button>
          </div>
        )}

        {/* Step 1: Email + Password */}
        {step === 1 && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Create your account</h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                You'll use these to sign in next time.
              </p>
            </div>

            <div className="rounded-2xl p-6 space-y-4 mb-6" style={glassCard}>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Email
                </label>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailTaken(false); }}
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl text-base focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                  }}
                  placeholder="you@example.com"
                />
                {email.trim().length > 0 && !emailValid && (
                  <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    Enter a full email address, like name@example.com.
                  </p>
                )}
                {emailTaken && (
                  <p className="text-xs mt-1.5" style={{ color: '#FCA5A5' }}>
                    An account with this email already exists.{' '}
                    <button type="button" onClick={() => navigate('/login')} className="underline font-semibold" style={{ color: '#fff' }}>
                      Sign in instead
                    </button>
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="signup-password" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-describedby="password-hints"
                    className="w-full px-4 py-3 pr-12 rounded-xl text-base focus:ring-2 focus:outline-none"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-all active:scale-95"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <PasswordHints id="password-hints" password={password} />
              </div>
            </div>

            {/* Terms agreement checkbox */}
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className="w-5 h-5 rounded flex items-center justify-center transition-all"
                  style={{
                    backgroundColor: termsAccepted ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                    border: termsAccepted ? '2px solid var(--brand-primary)' : '2px solid rgba(255,255,255,0.3)',
                  }}
                >
                  {termsAccepted && (
                    <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                      <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                I agree to the{' '}
                <a
                  href="https://dsp.cafe/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                  style={{ color: 'rgba(255,255,255,0.95)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Use
                </a>
                ,{' '}
                <a
                  href="https://dsp.cafe/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                  style={{ color: 'rgba(255,255,255,0.95)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                , and{' '}
                <a
                  href="https://dsp.cafe/guidelines.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                  style={{ color: 'rgba(255,255,255,0.95)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Community Guidelines
                </a>
              </span>
            </label>

            {continueHint && (
              <p role="status" className="text-xs text-center mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {continueHint}
              </p>
            )}

            <button
              onClick={handleEmailContinue}
              disabled={!emailPasswordValid || emailChecking || emailTaken}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-4 transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100 flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {emailChecking ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Checking...
                </>
              ) : 'Continue'}
            </button>

            <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-semibold underline"
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Sign in
              </button>
            </p>
          </div>
        )}

        {/* Step 2: About you — name + handle on one screen */}
        {step === 2 && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">About you</h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Your real name, and a handle other members can @mention.
              </p>
            </div>

            <div className="rounded-2xl p-6 mb-4 space-y-4" style={glassCard}>
              <div>
                <label htmlFor="onboarding-name" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Name
                </label>
                <input
                  id="onboarding-name"
                  ref={inputRef}
                  type="text"
                  value={userData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  autoComplete="name"
                  className="w-full px-4 py-3 rounded-xl text-lg focus:ring-2 focus:ring-brand-secondary focus:outline-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                  }}
                  placeholder="Your name"
                />
              </div>

              <div>
                {/* @ prefix input */}
                <label htmlFor="onboarding-username" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Username
                </label>
                <div className="relative flex items-center">
                  <span
                    className="absolute left-4 text-base font-medium select-none"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    @
                  </span>
                  <input
                    id="onboarding-username"
                    type="text"
                    value={username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    className="w-full pl-8 pr-10 py-3 rounded-xl text-lg focus:ring-2 focus:outline-none"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: `1px solid ${
                        usernameStatus === 'available' ? 'rgba(74,222,128,0.6)'
                        : usernameStatus === 'taken' || usernameStatus === 'reserved' || usernameStatus === 'invalid_format' ? 'rgba(248,113,113,0.6)'
                        : 'rgba(255,255,255,0.2)'
                      }`,
                      color: '#fff',
                    }}
                    placeholder="yourhandle"
                  />
                  {/* Status indicator */}
                  <div className="absolute right-3 flex items-center">
                    {usernameStatus === 'checking' && (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                    {usernameStatus === 'available' && (
                      <Check size={16} className="text-green-400" />
                    )}
                    {(usernameStatus === 'taken' || usernameStatus === 'reserved' || usernameStatus === 'invalid_format') && (
                      <X size={16} className="text-red-400" />
                    )}
                  </div>
                </div>

                {/* Feedback text */}
                {usernameStatus === 'available' && (
                  <p className="text-xs mt-1.5" style={{ color: 'rgba(74,222,128,0.9)' }}>Available!</p>
                )}
                {usernameStatus === 'taken' && (
                  <p className="text-xs mt-1.5" style={{ color: '#F87171' }}>That username is already taken.</p>
                )}
                {usernameStatus === 'reserved' && (
                  <p className="text-xs mt-1.5" style={{ color: '#F87171' }}>That name is reserved.</p>
                )}
                {usernameStatus === 'invalid_format' && (
                  <p className="text-xs mt-1.5" style={{ color: '#F87171' }}>Use 3–30 lowercase letters, numbers, or underscores.</p>
                )}
                {usernameStatus === 'idle' && username.length > 0 && username.length < 3 && (
                  <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>At least 3 characters.</p>
                )}
                {username.length === 0 && (
                  <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    Letters, numbers, and underscores only. 3–30 characters.
                  </p>
                )}
                {/* True: update_username allows a change every 30 days, and the
                    first set is exempt (011). */}
                <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  You can change this later in your profile.
                </p>
              </div>
            </div>

            {/* Display preference */}
            <div className="rounded-2xl p-5 mb-6" style={glassCard}>
              <p className="text-xs font-medium mb-3" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Appear publicly as
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setUseUsername(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98]"
                  style={{
                    backgroundColor: !useUsername ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)',
                    border: !useUsername ? '2px solid rgba(255,255,255,0.45)' : '2px solid rgba(255,255,255,0.12)',
                    color: '#fff',
                  }}
                >
                  Real name
                </button>
                <button
                  onClick={() => setUseUsername(true)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98]"
                  style={{
                    backgroundColor: useUsername ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)',
                    border: useUsername ? '2px solid rgba(255,255,255,0.45)' : '2px solid rgba(255,255,255,0.12)',
                    color: '#fff',
                  }}
                >
                  @username
                </button>
              </div>
              <p className="text-xs mt-2.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {useUsername
                  ? `Others will see "${username || 'yourhandle'}" on your posts and profile.`
                  : `Others will see "${userData.name || 'your name'}" on your posts and profile.`
                }
              </p>
            </div>

            <button
              onClick={nextStep}
              disabled={!userData.name.trim() || usernameStatus !== 'available'}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 3: Role */}
        {step === 3 && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">How do you support?</h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                This helps us personalize your experience.
              </p>
            </div>

            <div className="space-y-3 mb-5">
              {ROLE_OPTIONS.filter(r => r.dsp).map(r => ({ ...r, title: r.label })).map(option => (
                <button
                  key={option.id}
                  onClick={() => setUserData({ ...userData, role: option.id })}
                  className="w-full p-4 rounded-xl flex items-center gap-4 transition-all duration-200 active:scale-[0.98]"
                  style={{
                    ...glassCard,
                    border: userData.role === option.id
                      ? '2px solid rgba(255,255,255,0.5)'
                      : '1px solid rgba(255,255,255,0.18)',
                    backgroundColor: userData.role === option.id
                      ? 'rgba(255,255,255,0.18)'
                      : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <span className="text-3xl">{option.emoji}</span>
                  <div className="text-left">
                    <div className="font-semibold text-white">{option.title}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{option.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* 048: roles for people who aren't DSPs — agency staff, family,
                advocates. Compact so the DSP cards above stay the headline. */}
            <p className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>Not a DSP?</p>
            <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{DSP_CRITERION.notDsp}</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {ROLE_OPTIONS.filter(r => !r.dsp).map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setUserData({ ...userData, role: option.id })}
                  className="p-3 rounded-xl flex items-center gap-2 text-left transition-all duration-200 active:scale-[0.98]"
                  style={{
                    ...glassCard,
                    border: userData.role === option.id
                      ? '2px solid rgba(255,255,255,0.5)'
                      : '1px solid rgba(255,255,255,0.18)',
                    backgroundColor: userData.role === option.id
                      ? 'rgba(255,255,255,0.18)'
                      : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <span className="text-xl" aria-hidden="true">{option.emoji}</span>
                  <span className="text-sm font-semibold text-white leading-tight">{option.label}</span>
                </button>
              ))}
            </div>

            {/* Years of experience — as a DSP, so DSP roles only. */}
            {userData.role && isDspRole(userData.role) && (
              <div className="rounded-xl p-4 mb-2" style={glassCard}>
                <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Years of experience <span style={{ color: 'rgba(255,255,255,0.4)' }}>(optional)</span>
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={yearsExperience}
                    onChange={(e) => setYearsExperience(e.target.value)}
                    className="w-24 px-4 py-2.5 rounded-xl text-base focus:ring-2 focus:outline-none text-center"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                    placeholder="0"
                  />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>years</span>
                </div>
              </div>
            )}

            <button
              onClick={nextStep}
              disabled={!userData.role}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 4: Agency + zip, then join */}
        {step === 4 && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {agencyStepCopy(userData.role).title}
              </h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {agencyStepCopy(userData.role).subtitle}
              </p>
            </div>

            <div className="rounded-2xl p-6 mb-4 space-y-4" style={glassCard}>
              <input
                ref={inputRef}
                type="text"
                value={userData.agency}
                onChange={(e) => setUserData({ ...userData, agency: e.target.value })}
                className="w-full px-4 py-3 rounded-xl text-lg focus:ring-2 focus:ring-brand-secondary focus:outline-none"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                }}
                placeholder={agencyStepCopy(userData.role).placeholder}
              />
              {/* 054: opt-in zip for counting DSPs by area. DSP roles only —
                  the count is of DSPs. Entering it is the opt-in. */}
              {isDspRole(userData.role) && (
                <div>
                  <label htmlFor="onboarding-zip" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Zip code <span style={{ color: 'rgba(255,255,255,0.4)' }}>(optional)</span>
                  </label>
                  <input
                    id="onboarding-zip"
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={5}
                    value={zipCode}
                    onChange={(e) => setZipCode(cleanZip(e.target.value))}
                    className="w-full px-4 py-3 rounded-xl text-lg focus:ring-2 focus:ring-brand-secondary focus:outline-none"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                    placeholder="5-digit zip"
                  />
                  {zipCode !== '' && !isValidZip(zipCode) && (
                    <p className="text-xs mt-1.5" style={{ color: '#FCA5A5' }}>Enter all 5 digits, or leave it blank.</p>
                  )}
                  <p className="text-xs mt-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {ZIP_NOTICE}
                  </p>
                </div>
              )}
            </div>

            {signUpError && (
              <p className="text-sm text-center mb-4" style={{ color: '#F87171' }}>
                {signUpError}
              </p>
            )}

            <button
              onClick={() => handleJoin()}
              disabled={submitting || (isDspRole(userData.role) && zipCode !== '' && !isValidZip(zipCode))}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-3 disabled:opacity-40 transition-all duration-200 hover:brightness-110 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Join the Community
                  <ChevronRight size={20} />
                </>
              )}
            </button>

            {/* Skipping must not submit a zip — submitting is the opt-in. */}
            <button
              onClick={() => { setZipCode(''); handleJoin({ skipZip: true }); }}
              disabled={submitting}
              className="w-full py-2 text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-40"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              Skip and join
            </button>

            <p className="text-xs text-center mt-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Your data is never sold
            </p>
          </div>
        )}

        {/* Step 5: Check your email — email confirmation is on, so signUp
            returned no session. Nothing is lost: pending_signup is applied on
            the first sign-in after confirming. */}
        {step === CHECK_EMAIL_STEP && (
          <div className="flex-1 flex flex-col justify-center splash-entrance">
            <div className="mb-6">
              <div
                className="w-12 h-12 mb-4 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <Mail size={24} className="text-white" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                We sent a link to <span className="font-semibold text-white break-all">{email.trim()}</span>.
                Tap it to confirm your address, then sign in.
              </p>
            </div>

            <div className="rounded-2xl p-5 mb-6 space-y-2" style={glassCard}>
              <p className="text-sm text-white">Don&rsquo;t see it?</p>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                It can take a minute. Check your spam or promotions folder for an email from DSP Cafe.
              </p>
            </div>

            {confirmState === 'unconfirmed' && (
              <p className="text-sm text-white rounded-xl p-3 mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} role="status">
                Your confirmation hasn&rsquo;t come through yet. The email can take a minute to arrive, and it may be
                in your spam folder. If you&rsquo;re still having trouble finding it, use the button below and
                we&rsquo;ll send it again.
              </p>
            )}
            <button
              onClick={handleConfirmedSignIn}
              disabled={confirmState === 'checking'}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-3 transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {confirmState === 'checking' ? 'Signing in…' : <>I&rsquo;ve confirmed, sign in</>}
            </button>
            <button
              onClick={handleResend}
              disabled={resendState === 'sending' || resendState === 'sent'}
              className="w-full py-2 text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ color: 'rgba(255,255,255,0.75)' }}
            >
              {resendState === 'sending' ? 'Sending…' : resendState === 'sent' ? 'Sent again' : 'Send the email again'}
            </button>
            {resendState === 'error' && (
              <p className="text-xs text-center mt-1" style={{ color: '#FCA5A5' }}>
                That didn&rsquo;t send. Wait a minute and try again.
              </p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default OnboardingScreen;
